import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useDarkTheme } from "@/contexts/DarkTheme";
import {
  formatToDecimalPlaces,
  getIsFxStrategy,
  Pagination,
  randomString,
  useWindowWidth,
} from "@/helpers/helperFunctions";
import { SharpRatioString } from "@/helpers/types";
import { cn } from "@/lib/utils";
import debounce from "lodash/debounce";
import { Eye, Layers2 } from "lucide-react";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";
import { BiSolidHelpCircle } from "react-icons/bi";
import { CgArrowBottomRight, CgArrowTopRight } from "react-icons/cg";
import { FaArrowTrendDown, FaArrowTrendUp } from "react-icons/fa6";
import Slider from "react-slick";
import Sort from "../Sort/index";

import { toast } from "@/components/ui/use-toast";
import { POLYGON_IO_URL } from "@/config";
import ViewModal from "@/sections/build/components/StrategyViewModal";
import ViewImageModal from "@/sections/build/components/ViewImageModal";
import { PIO_CONST, SIOSubscription } from "@/config/polygon-constants";
import Image from "next/image";
import { io } from "socket.io-client";
import { useGetSymbolData, useGetTickerSnapshot } from "@/api/symbolApi";
import { Label } from "@/components/ui/label";
import { usePostCopyStrategy } from "@/api/strategyApi";
import SymbolUpdateModal from "@/components/common/SymbolUpdateModal";
const index = ({
  data,
  logos,
  totalDataLength,
  page,
  totalPages,
  setPage,
  dataSize,
  filters,
  setFilters,
  symbols,
  buildPage,
  suggestion,
  isPopup,
}: any) => {
  const { t } = useTranslation("marketplace");
  const { darkTheme } = useDarkTheme();

  const settings = {
    className: "variable-width",
    arrows: true,
    dots: false,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: true,
    speed: 500,
    initialSlide: 0,
    infinite: false,
  };
  const router = useRouter();
  const [symbol, setSymbol] = useState<string | undefined>(undefined);
  const { slug } = router.query;

  useEffect(() => {
    if (slug) {
      setSymbol(Array.isArray(slug) ? slug[0] : slug);
    }
  }, [slug]);

  let [userInfo, setUserInfo]: any = useState({});
  useEffect(() => {
    if (typeof window !== undefined) {
      const u = JSON.parse(localStorage.getItem("user-info")!);
      setUserInfo(u);
    }
  }, []);
  const [currentCopyStrategy, setCurrentCopyStrategy] = useState<any>(null);
  const polygonSocket = useMemo(() => io(POLYGON_IO_URL), []);
  const [selected, setSelected] = useState<any>({});
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSymbolUpdateModal, setShowSymbolUpdateModal] =
    useState<boolean>(false);

  let [livePriceData, setLivePriceData]: any = useState({});
  const pioDataRef = useRef<string[]>([]);
  const screenWidth = useWindowWidth();
  const {
    data: tickerSnapshot,
    isLoading: priceLoading,
    isSuccess: isSuccessTickerSnapShot,
  } = useGetTickerSnapshot(symbols.toString());

  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
    isPending: isCopyPending,
  } = usePostCopyStrategy();

  const { mutateAsync: getSymbolData, isPending: isCurrentPriceLoading } =
    useGetSymbolData();

  useEffect(() => {
    if (isSuccessTickerSnapShot && tickerSnapshot) {
      setLivePriceData(tickerSnapshot.data);
    }
  }, [isSuccessTickerSnapShot, tickerSnapshot]);

  useEffect(() => {
    const handleSocketData = (topic: string, data: any, oldData: any) => {
      if (Object.keys(oldData).includes(data.pair)) {
        let updatedLiveData = { ...oldData };
        let updatedStock = updatedLiveData[data.pair];
        updatedStock.lastQuote.p = data.c;
        setLivePriceData((prev: any) => {
          const updatedData = { ...prev };
          updatedData[data.pair] = updatedStock;
          return updatedData;
        });
      }
    };
    let handleConnect = () => {
      polygonSocket.emit(PIO_CONST.EVENT_NAME.SUBSCRIBE, {
        symbols: pioDataRef.current,
        type: PIO_CONST.SUBS_TYPE.FMV,
      } as SIOSubscription);
      polygonSocket.emit(PIO_CONST.EVENT_NAME.SUBSCRIBE, {
        symbols: pioDataRef.current,
        type: PIO_CONST.SUBS_TYPE.BLOFIN,
      } as SIOSubscription);
      polygonSocket.emit(PIO_CONST.EVENT_NAME.SUBSCRIBE, {
        symbols: pioDataRef.current,
        type: PIO_CONST.SUBS_TYPE.AGGREGATOR_FX,
      } as SIOSubscription);
    };
    polygonSocket.on("connect", handleConnect);

    polygonSocket.on(PIO_CONST.SUBS_TOPICS.FMV, (data: any) =>
      handleSocketData(PIO_CONST.SUBS_TOPICS.FMV, data, livePriceData)
    );
    polygonSocket.on(PIO_CONST.SUBS_TOPICS.AGGREGATOR_FX, (data: any) =>
      handleSocketData(PIO_CONST.SUBS_TOPICS.AGGREGATOR_FX, data, livePriceData)
    );
    polygonSocket.on(PIO_CONST.SUBS_TOPICS.AGGREGATOR_CRYPTO, (data: any) =>
      handleSocketData(
        PIO_CONST.SUBS_TOPICS.AGGREGATOR_CRYPTO,
        data,
        livePriceData
      )
    );

    return () => {
      polygonSocket.off(PIO_CONST.SUBS_TOPICS.FMV);
      polygonSocket.off(PIO_CONST.SUBS_TOPICS.AGGREGATOR_FX);
      polygonSocket.off(PIO_CONST.SUBS_TOPICS.AGGREGATOR_CRYPTO);
    };
  }, [livePriceData]);

  useEffect(() => {
    if (isCopySuccess && copyStrategyData) {
      toast({
        title: t("Strategy copied successfully"),
        variant: "default",
      });
      setTimeout(() => {
        router.push(
          "/edit-strategy/" +
            encodeURIComponent(userInfo?.user_id) +
            "/" +
            copyStrategyData?.data?.id +
            randomString(5) +
            "/" +
            userInfo.token +
            "/false" +
            `?ref=${router.asPath}&locale=${router.query.locale || "en"}`
        );
      }, 2000);
    }
  }, [isCopySuccess, tickerSnapshot, isCopyPending]);

  const copyHandler = async (item: any, userId: string) => {
    if (!userId) {
      toast({
        title: t("Login Required"),
        description: t("Please log in to copy this strategy."),
        variant: "destructive",
      });
      return;
    }
    try {
      const res = await getSymbolData({
        user_id: userId,
        ticker: item?.strategy.symbol,
      });
      if (!res?.data || res?.data?.regularMarketPrice == 0) {
        setShowSymbolUpdateModal(true);
        setCurrentCopyStrategy(item?.strategy);
      } else {
        await copyStrategy({
          userId,
          key: item?.strategy.keygen,
          symbol: item?.strategy.symbol,
        });
      }
    } catch (error) {
      toast({
        title: t("Error copying strategy"),
        variant: "destructive",
      });
    }
  };

  let [showImage, setShowImage] = useState(false);
  let [imageLink, setImageLink] = useState("");

  return (
    <div className="flex  justify-between flex-col md:flex-row gap-4 w-full h-full">
      <ViewModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        selected={selected}
        token={userInfo?.token}
      />
      <ViewImageModal
        isOpen={showImage}
        onRequestClose={() => setShowImage(false)}
        link={imageLink}
      />
      <SymbolUpdateModal
        isOpen={showSymbolUpdateModal}
        strategy={currentCopyStrategy}
        setIsOpen={setShowSymbolUpdateModal}
      ></SymbolUpdateModal>
      <Card className={`!w-full  h-full ${buildPage && "border-none p-0 m-0"}`}>
        {!buildPage && (
          <CardHeader className="flex !p-3 px-6 justify-between flex-row items-center w-full">
            <CardTitle className="text-xl flex gap-2 items-center text-nowrap ">
              <h1>
                {t("Top Strategies")}
                {filters?.symbol || symbol
                  ? ` for ${symbol || filters.symbol}`
                  : ""}
              </h1>
            </CardTitle>
            <Sort filters={filters} setFilters={setFilters} />
          </CardHeader>
        )}

        <CardContent className={`p-3 pt-0 w-full`}>
          <div
            className={`${data.length < 10 ? "h-auto" : "h-[2560px]"} w-full`}
          >
            {data?.map((item: any, index: number) => {
              let badges = [
                item?.meta_data?.strategy_meta.actions,
                item?.meta_data?.strategy_meta.datas,
                item?.meta_data?.strategy_meta.indicators,
                item?.meta_data?.strategy_meta.logics,
              ].reduce((acc, curr) => {
                return acc.concat(curr);
              }, []);
              const canvasData = item?.strategy?.canvas_data
                ? JSON.parse(item?.strategy?.canvas_data)
                : {};
              const nodes = canvasData?.filter?.(
                (item: any) => item.type === "selectorNode"
              );
              const edges = canvasData?.filter?.(
                (item: any) => item.type === "buttonedge"
              );
              let todaysChange =
                livePriceData[item?.strategy?.symbol]?.todaysChange;
              if (todaysChange !== "-") {
                todaysChange = todaysChange?.toFixed(2);
              }
              let percentageChange =
                livePriceData[item?.strategy?.symbol]?.todaysChangePerc;
              if (percentageChange !== "-") {
                percentageChange = percentageChange?.toFixed(2);
              }

              let category = Object.keys(
                item?.meta_data?.strategy_meta?.indicator_category
              ).join(", ");
              let maxTakeProfit = item?.meta_data?.tpsl?.take_profit_p_max;
              let stopLoss = item?.meta_data?.tpsl?.stop_loss_p_max;
              let actions: any = new Set(
                item?.meta_data?.strategy_meta?.actions
              );
              actions = Array.from(actions);
              let actionOptions: any = {
                Buy: "Long",
                Sell: "Short",
                "Smart Buy": "Smart Buy",
                "Smart Sell": "Smart Sell",
                "Smart Exit Position": "Smart Exit",
              };

              return (
                <Card
                  key={item?.strategy?.symbol + "_" + index}
                  className={cn(
                    "flex flex-col h-auto lg:flex-row lg:h-[245px]  ",
                    suggestion || index != 0 ? "mt-3" : "",
                    isPopup && "border-none"
                  )}
                >
                  <div
                    className={`h-full ${
                      item.strategy.cached && screenWidth > 1600 && !isPopup
                        ? "w-full lg:w-[60%]"
                        : "w-full"
                    }`}
                  >
                    <CardHeader className={`p-4 pr-0 pb-3`}>
                      <div className="flex flex-col sm:flex-row">
                        <div className="flex flex-col justify-between">
                          <CardTitle className="text-md flex items-center gap-2">
                            {logos?.get(item.symbol) !== undefined ? (
                              <img
                                src={logos.get(item.symbol)}
                                style={{
                                  maxHeight: "30px",
                                  maxWidth: "30px",
                                }}
                                alt={item.strategy.symbol}
                              />
                            ) : (
                              <img
                                src={
                                  darkTheme
                                    ? "/images/logos/Symbol_Dark.png"
                                    : "/images/logos/Symbol_Light-30.png"
                                }
                                style={{
                                  maxHeight: "30px",
                                  maxWidth: "30px",
                                }}
                                alt={item.strategy.symbol}
                              />
                            )}
                            <div className="text-muted-foreground">
                              {item.strategy.symbol}
                            </div>

                            <div className="pl-1 text-[12px] text-muted-foreground">
                              {item.strategy.timeframe}
                            </div>
                          </CardTitle>
                          {category && (
                            <div className="bg-[--marketplace-blue-new] rounded-xl text-[10px] p-[2px] pl-2 pr-2 mt-2 mb-2 sm:mt-4 sm:mb-0 text-white max-w-fit">
                              {category}
                            </div>
                          )}
                        </div>

                        <div className="flex items-end sm:ml-[5%] gap-6">
                          <div className="flex flex-col  gap-1 ">
                            {item?.backtest_data?.win >= 75 ? (
                              <div className="text-3xl font-semibold text-[--marketplace-green]">
                                {formatToDecimalPlaces(
                                  item?.backtest_data?.win,
                                  0
                                )}
                                %
                              </div>
                            ) : item?.backtest_data?.win >= 50 ? (
                              <div className="text-3xl font-semibold text-[--marketplace-blue]">
                                {formatToDecimalPlaces(
                                  item?.backtest_data?.win,
                                  0
                                )}
                                %
                              </div>
                            ) : item?.backtest_data?.win >= 0 ? (
                              <div className="text-3xl font-semibold text-[--marketplace-red]">
                                {formatToDecimalPlaces(
                                  item?.backtest_data?.win,
                                  0
                                )}
                                %
                              </div>
                            ) : (
                              "NA"
                            )}

                            <div className="flex items-center gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                              Average Win
                              {/* <HoverCard>
                            <HoverCardTrigger>
                              <BiSolidHelpCircle size={16} />
                            </HoverCardTrigger>
                            <HoverCardContent>
                              {t(
                                "The percentage of trades that ended in profit. A higher Win % means more successful trades."
                              )}
                            </HoverCardContent>
                          </HoverCard> */}
                            </div>
                          </div>
                          {actions.length > 0 && (
                            <div className="flex flex-col  gap-1 ">
                              <div className="flex text-sm font-semibold">
                                {actions.map((each: string, index: string) => {
                                  if (actionOptions[each] === "Long") {
                                    return (
                                      <div
                                        className="text-green-foreground flex items-end"
                                        key={each + "_" + index}
                                      >
                                        {actionOptions[each]}{" "}
                                        <CgArrowTopRight className="text-sm" />
                                      </div>
                                    );
                                  }

                                  if (actionOptions[each] === "Short") {
                                    return (
                                      <div
                                        className=" text-red-foreground flex items-end"
                                        key={each + "_" + index}
                                      >
                                        {actionOptions[each]}{" "}
                                        <CgArrowBottomRight className="text-sm" />
                                      </div>
                                    );
                                  }

                                  if (actionOptions[each] === "Smart Buy") {
                                    return (
                                      <div
                                        className="text-green-foreground flex items-end"
                                        key={each + "_" + index}
                                      >
                                        {actionOptions[each]}{" "}
                                        <FaArrowTrendUp className="text-sm" />
                                      </div>
                                    );
                                  }
                                  if (actionOptions[each] === "Smart Sell") {
                                    return (
                                      <div
                                        className=" text-red-foreground flex items-end"
                                        key={each + "_" + index}
                                      >
                                        {actionOptions[each]}{" "}
                                        <FaArrowTrendDown className="text-sm ml-[2px]" />
                                      </div>
                                    );
                                  }
                                })}
                              </div>
                              <div className="flex text-[11px] items-center gap-0.5 text-[var(--marketplace-grey)] font-semibold">
                                Actions
                              </div>
                            </div>
                          )}
                          {!isPopup && (
                            <>
                              {maxTakeProfit >= 0 && (
                                <div className="flex flex-col gap-1 text-xs">
                                  <div className="text-foreground text-green-foreground text-sm font-semibold">
                                    {maxTakeProfit}%
                                  </div>
                                  <div className="flex items-center gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                                    Take Profit
                                  </div>
                                </div>
                              )}
                              {stopLoss >= 0 && (
                                <div className="flex flex-col gap-1 text-xs">
                                  <div className="text-foreground text-red-foreground text-sm font-semibold">
                                    {stopLoss}%
                                  </div>
                                  <div className="flex items-center gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                                    Stop Loss
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent
                      className={`p-4 pr-0 pt-2 flex flex-col gap-3`}
                    >
                      <div className="border-b flex flex-col gap-2 pb-2">
                        <div className="flex items-center relative mb-3">
                          <div className="text-sm text-muted-foreground font-bold">
                            Statistics
                          </div>
                          {!isPopup && (
                            <div
                              className={`w-[70%] absolute right-6 top-0 bottom-0 pr-5`}
                            >
                              <Slider {...settings}>
                                {badges?.map((item: any, id: number) => {
                                  return (
                                    <div className="mr-2" key={id}>
                                      <Badge variant="outline">{item}</Badge>
                                    </div>
                                  );
                                })}
                              </Slider>
                            </div>
                          )}
                        </div>

                        <div className="flex text-xs gap-6">
                          <div className="flex flex-col gap-1">
                            <div className="flex gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                              Drawdown{" "}
                              <HoverCard>
                                <HoverCardTrigger>
                                  <BiSolidHelpCircle size={16} />
                                </HoverCardTrigger>
                                <HoverCardContent>
                                  How much your strategy drops from its highest
                                  point to its lowest to measure risk - smaller
                                  drops mean less risk, bigger drops mean more
                                  risk.
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                            <div>
                              $
                              {formatToDecimalPlaces(
                                item?.backtest_data?.dd,
                                2
                              )}
                            </div>
                          </div>
                          <div className="flex flex-col  gap-1">
                            <div className="flex gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                              Sharpe Ratio
                              <HoverCard>
                                <HoverCardTrigger>
                                  <BiSolidHelpCircle size={16} />
                                </HoverCardTrigger>
                                <HoverCardContent className="flex flex-col gap-1">
                                  The risk-adjusted return of your strategy,
                                  helping you assess the quality of your trading
                                  strategy overall.
                                  <span>
                                    {
                                      "<1: Poor – Returns are not compensating for the risk taken."
                                    }
                                  </span>
                                  <span>
                                    {
                                      "1: Good – Acceptable risk-adjusted returns."
                                    }
                                  </span>
                                  <span>
                                    {
                                      "2: Very Good – Strong performance with solid risk management."
                                    }
                                  </span>
                                  <span>
                                    {
                                      ">3: Excellent – Exceptional risk-adjusted returns."
                                    }
                                  </span>
                                </HoverCardContent>
                              </HoverCard>
                            </div>
                            <div
                              className={cn(
                                "",
                                item?.backtest_data?.sr ===
                                  SharpRatioString.BELOW3
                                  ? "text-red-foreground"
                                  : item?.backtest_data?.sr ===
                                    SharpRatioString.ABOVE3
                                  ? "text-green-foreground"
                                  : "text-foreground"
                              )}
                            >
                              {item?.backtest_data?.sr}
                            </div>
                          </div>
                          {isPopup && (
                            <>
                              {maxTakeProfit >= 0 && (
                                <div className="flex flex-col gap-1 text-xs">
                                  <div className="text-green-foreground text-sm font-semibold">
                                    {maxTakeProfit}%
                                  </div>
                                  <div className="flex items-center gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                                    Take Profit
                                  </div>
                                </div>
                              )}
                              {stopLoss >= 0 && (
                                <div className="flex flex-col gap-1 text-xs">
                                  <div className=" text-red-foreground text-sm font-semibold">
                                    {stopLoss}%
                                  </div>
                                  <div className="flex items-center gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                                    Stop Loss
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                          {!isPopup && (
                            <div className="flex  flex-col gap-1">
                              <div className="flex gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                                Copier Count
                              </div>
                              <div className="text-foreground">
                                {item?.meta_data?.copiers_count}
                              </div>
                            </div>
                          )}
                          {!!item?.deployment_data?.quantity && (
                            <div className="flex flex-col gap-1">
                              <div className="flex gap-0.5 text-[11px] text-[var(--marketplace-grey)] font-semibold">
                                Quantity used
                                <HoverCard>
                                  <HoverCardTrigger>
                                    <BiSolidHelpCircle size={16} />
                                  </HoverCardTrigger>
                                  <HoverCardContent className="flex flex-col gap-1">
                                    Quantity used by the user at the time of
                                    deploying this strategy.
                                  </HoverCardContent>
                                </HoverCard>
                              </div>
                              <div>
                                {item?.deployment_data?.quantity
                                  ? Math.max(item?.deployment_data?.quantity, 1)
                                  : 1}
                              </div>
                            </div>
                          )}
                        </div>
                        {/* </div> */}
                      </div>
                    </CardContent>
                    <CardFooter className={`flex justify-between pb-4`}>
                      <Label className="text-muted-foreground font-semibold">
                        {item?.strategy?.strategy_name}
                      </Label>

                      <div className="flex gap-2 ">
                        <div
                          className="mr-2 text-sm font-bold cursor-pointer text-muted-foreground hover:text-[#545454]"
                          onClick={() => {
                            if (item?.strategy?.cached) {
                              setShowImage(true);
                              setImageLink(item?.strategy?.cached);
                            } else {
                              setSelected({
                                nodes,
                                edges,
                                symbol: item.strategy.symbol,
                                timeframe: item.strategy.timeframe,
                              });
                              setShowModal(true);
                            }
                          }}
                        >
                          <div className="flex items-center ">
                            <div>
                              <Eye className="w-[15px]" />
                            </div>
                            <div className="ml-1">View</div>
                          </div>
                        </div>
                        {/* //replace it is forex strategy to disable when forex */}
                        {true ? (
                          userInfo?.user_id ? (
                            <Button
                              loading={isCopyPending || isCurrentPriceLoading}
                              variant={"ghost"}
                              className="text-sm font-bold cursor-pointer text-muted-foreground hover:bg-transparent hover:text-[#545454] p-0 m-0 h-auto"
                              onClick={() =>
                                copyHandler(item, userInfo?.user_id)
                              }
                            >
                              <div className="flex items-center">
                                <div>
                                  <Layers2 className="w-[15px]" />
                                </div>
                                <div className="ml-1">Copy</div>
                              </div>
                            </Button>
                          ) : (
                            <HoverCard>
                              <HoverCardTrigger asChild>
                                <div className="text-sm font-bold cursor-pointer text-muted-foreground hover:text-[#545454]">
                                  <div className="flex items-center">
                                    <div>
                                      <Layers2 className="w-[15px]" />
                                    </div>
                                    <div className="ml-1">Copy</div>
                                  </div>
                                </div>
                              </HoverCardTrigger>
                              <HoverCardContent className="w-80">
                                <div className="flex justify-between space-x-4">
                                  <div className="space-y-1">
                                    <p className="text-sm">
                                      You need to create an account with us to
                                      access this feature
                                    </p>
                                    <div className="mt-5 w-[100%] relative h-[42px]">
                                      <div className="absolute right-0 top-2">
                                        <a href={`/create-new-account`}>
                                          <Button className="flex items-center h-[34px]">
                                            Sign Up
                                          </Button>
                                        </a>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </HoverCardContent>
                            </HoverCard>
                          )
                        ) : (
                          <HoverCard>
                            <HoverCardTrigger>
                              <div className="text-sm font-bold cursor-pointer text-muted hover:text-[#545454]">
                                <div className="flex items-center">
                                  <div>
                                    <Layers2 className="w-[15px]" />
                                  </div>
                                  <div className="ml-1">Copy</div>
                                </div>
                              </div>
                            </HoverCardTrigger>
                            <HoverCardContent className="w-40">
                              This asset class is not currently accessible for
                              your account
                            </HoverCardContent>
                          </HoverCard>
                        )}
                      </div>
                    </CardFooter>
                  </div>
                  {item?.strategy?.cached && screenWidth > 1280 && !isPopup && (
                    <div className="w-full lg:w-[50%] h-full">
                      <div
                        className="relative w-full h-full"
                        onClick={() => {
                          setShowImage(true);
                          setImageLink(item?.strategy?.cached);
                        }}
                      >
                        <Image
                          src={item.strategy.cached}
                          alt="image"
                          width={0}
                          height={0}
                          sizes="100%"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          className="rounded-r-lg"
                        />
                      </div>
                    </div>
                  )}
                </Card>
              );
            })}

            <div className="mt-3">
              <Pagination
                totalCount={totalDataLength}
                currentPage={page}
                changePage={setPage}
                limit={dataSize}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default index;
